import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  ClipboardList,
  CheckCircle2,
  XCircle,
  Receipt,
  FileText,
  Inbox,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { useExpenses } from "../../lib/expenses/useExpenses";
import { useInvoices } from "../../lib/invoices/useInvoices";
import { useOrg } from "../../lib/org/useOrg";
import { useCapabilities } from "../../lib/permissions/useCapabilities";
import { useAuth } from "../../lib/auth/useAuth";
import { isSupabaseConfigured, supabase } from "../../lib/supabase";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import type { ExpenseStatus } from "../../lib/expenses/types";
import type { InvoiceStatus } from "../../lib/invoices/types";

// ---------------------------------------------------------------------------
// /review — admin/owner queue for pending expenses + invoices.
//
// Wave 17 additions:
//   - Status filter tabs: All | Pending | Approved | Rejected
//   - Submitter name resolution from org members (email prefix fallback)
//   - "0 items pending" empty state with icon
//   - Admin/owner can approve their own submissions (self-approval)
// ---------------------------------------------------------------------------

type ReviewStatusFilter = "all" | "pending" | "approved" | "rejected";

const FILTER_LABELS: Record<ReviewStatusFilter, string> = {
  all: "All",
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
};

const EXPENSE_STATUSES: ExpenseStatus[] = ["pending", "approved", "rejected"];
const INVOICE_STATUSES: InvoiceStatus[] = ["pending", "approved", "rejected"];

type Row =
  | {
      kind: "expense";
      id: string;
      vendor: string;
      amountCents: number;
      currency: string;
      submittedBy: string | null;
      submittedAt: string | null;
      status: ExpenseStatus;
    }
  | {
      kind: "invoice";
      id: string;
      vendor: string;
      amountCents: number;
      currency: string;
      submittedBy: string | null;
      submittedAt: string | null;
      status: InvoiceStatus;
    };

function formatAmount(cents: number, currency = "USD"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(cents / 100);
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return format(parseISO(iso), "MMM d, yyyy");
  } catch {
    return iso;
  }
}

function StatusBadge({ status }: { status: string }): React.ReactElement {
  switch (status) {
    case "pending":
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
          Pending
        </span>
      );
    case "approved":
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
          Approved
        </span>
      );
    case "rejected":
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
          Rejected
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
          {status}
        </span>
      );
  }
}

// ---------------------------------------------------------------------------
// Member name lookup hook
// ---------------------------------------------------------------------------

interface MemberInfo {
  userId: string;
  email: string;
}

function useOrgMemberNames(): Map<string, string> {
  const { activeOrg } = useOrg();
  const [members, setMembers] = useState<MemberInfo[]>([]);

  const loadMembers = useCallback(async () => {
    if (!activeOrg || !isSupabaseConfigured) {
      setMembers([]);
      return;
    }
    const { data } = await supabase.rpc("org_members_with_emails", {
      target_org: activeOrg.id,
    });
    if (!data) return;
    const rows = data as Array<{ user_id: string; email: string | null }>;
    setMembers(rows.map((r) => ({ userId: r.user_id, email: r.email ?? "" })));
  }, [activeOrg]);

  useEffect(() => {
    void loadMembers();
  }, [loadMembers]);

  return useMemo(() => {
    const map = new Map<string, string>();
    for (const m of members) {
      if (m.email) map.set(m.userId, m.email);
    }
    return map;
  }, [members]);
}

function submitterDisplay(
  userId: string | null,
  memberMap: Map<string, string>,
): string {
  if (!userId) return "—";
  const email = memberMap.get(userId);
  if (email) {
    const atIdx = email.indexOf("@");
    return atIdx > 0 ? email.slice(0, atIdx) : email;
  }
  return userId.length > 8 ? `${userId.slice(0, 8)}…` : userId;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

// Wave 21: `embedded` is passed when Review is rendered inside the Inbox
// tab bar. It suppresses the non-admin redirect (Inbox already gates) and
// locks the internal filter to "pending" (Inbox owns the outer tab bar).
export interface ReviewProps {
  embedded?: boolean;
}

export function Review({ embedded = false }: ReviewProps = {}) {
  const { activeRole } = useOrg();
  const { can } = useCapabilities();
  const { user } = useAuth();
  const navigate = useNavigate();
  const memberMap = useOrgMemberNames();

  const {
    expenses,
    approve: approveExpense,
    reject: rejectExpense,
  } = useExpenses({ status: EXPENSE_STATUSES });
  const {
    invoices,
    approve: approveInvoice,
    reject: rejectInvoice,
  } = useInvoices({ status: INVOICE_STATUSES });

  // Phase 1: Review queue gate is capability-driven. A user with either
  // approve:expenses or approve:invoices can open the page. `activeRole`
  // kept in the dep list so the guard still triggers once the session
  // resolves the role (and therefore the bundle).
  const canReview = can("approve:expenses") || can("approve:invoices");

  useEffect(() => {
    // Only run the guard when mounted standalone. Inbox has already rewritten
    // the URL for non-admins when tab=pending is requested.
    if (embedded) return;
    if (activeRole != null && !canReview) {
      navigate("/inbox", { replace: true });
    }
  }, [embedded, activeRole, canReview, navigate]);

  const [statusFilter, setStatusFilter] =
    useState<ReviewStatusFilter>("pending");

  const allRows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    for (const e of expenses) {
      out.push({
        kind: "expense",
        id: e.id,
        vendor: e.vendor,
        amountCents: e.amountCents,
        currency: e.currency,
        submittedBy: e.submittedBy,
        submittedAt: e.submittedAt,
        status: e.status,
      });
    }
    for (const i of invoices) {
      out.push({
        kind: "invoice",
        id: i.id,
        vendor: i.counterparty,
        amountCents: i.amountCents,
        currency: i.currency,
        submittedBy: i.submittedBy,
        submittedAt: i.submittedAt,
        status: i.status,
      });
    }
    out.sort((a, b) =>
      (b.submittedAt ?? "").localeCompare(a.submittedAt ?? ""),
    );
    return out;
  }, [expenses, invoices]);

  const rows = useMemo<Row[]>(() => {
    if (statusFilter === "all") return allRows;
    return allRows.filter((r) => r.status === statusFilter);
  }, [allRows, statusFilter]);

  const pendingCount = useMemo(
    () => allRows.filter((r) => r.status === "pending").length,
    [allRows],
  );

  const [rejectingRow, setRejectingRow] = useState<Row | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  async function handleApprove(row: Row): Promise<void> {
    setBusy(row.id);
    try {
      if (row.kind === "expense") await approveExpense(row.id);
      else await approveInvoice(row.id);
    } finally {
      setBusy(null);
    }
  }

  function openReject(row: Row): void {
    setRejectingRow(row);
    setRejectReason("");
  }

  async function submitReject(): Promise<void> {
    if (!rejectingRow) return;
    const reason = rejectReason.trim();
    if (!reason) return;
    setBusy(rejectingRow.id);
    try {
      if (rejectingRow.kind === "expense")
        await rejectExpense(rejectingRow.id, reason);
      else await rejectInvoice(rejectingRow.id, reason);
      setRejectingRow(null);
      setRejectReason("");
    } finally {
      setBusy(null);
    }
  }

  const currentUserId = user?.id ?? null;

  return (
    <div data-testid="review-queue" className="max-w-5xl mx-auto space-y-6">
      {!embedded && (
        <header>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-[#0070F2]" /> Review queue
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Approve or reject pending expenses and invoices for your organization.
          </p>
        </header>
      )}

      {/* Status filter tabs — hidden when embedded in Inbox, which owns the outer tab bar. */}
      {!embedded && (
        <div className="flex items-center gap-1 border-b border-slate-200">
          {(Object.keys(FILTER_LABELS) as ReviewStatusFilter[]).map((s) => {
            const count =
              s === "all"
                ? allRows.length
                : allRows.filter((r) => r.status === s).length;
            return (
              <button
                key={s}
                type="button"
                data-testid={`review-filter-${s}`}
                onClick={() => setStatusFilter(s)}
                className={[
                  "px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
                  statusFilter === s
                    ? "border-[#0070F2] text-[#0070F2]"
                    : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300",
                ].join(" ")}
              >
                {FILTER_LABELS[s]}
                {count > 0 && (
                  <span
                    className={`ml-1.5 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                      s === "pending"
                        ? "bg-amber-100 text-amber-700"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {rows.length === 0 ? (
          <div
            data-testid="review-empty"
            className="flex flex-col items-center justify-center gap-3 text-center px-6 py-16 text-slate-500"
          >
            <Inbox className="w-10 h-10 text-slate-300" />
            {statusFilter === "pending" ? (
              <>
                <div className="text-sm font-medium text-slate-700">
                  0 items pending
                </div>
                <div className="text-xs text-slate-500">
                  All submissions have been reviewed. New uploads waiting for
                  approval will appear here.
                </div>
              </>
            ) : (
              <>
                <div className="text-sm font-medium text-slate-700">
                  No {statusFilter === "all" ? "" : statusFilter} items
                </div>
                <div className="text-xs text-slate-500">
                  Nothing to show for this filter.
                </div>
              </>
            )}
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2">Vendor / Counterparty</th>
                <th className="px-4 py-2">Amount</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Submitted by</th>
                <th className="px-4 py-2">Submitted on</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => {
                const isSelf =
                  currentUserId != null &&
                  row.submittedBy === currentUserId;
                const isPending = row.status === "pending";
                return (
                  <tr
                    key={`${row.kind}:${row.id}`}
                    data-testid={`review-row-${row.id}`}
                    className="hover:bg-slate-50/60"
                  >
                    <td className="px-4 py-3">
                      {row.kind === "expense" ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 bg-slate-100 px-2 py-0.5 rounded">
                          <Receipt className="w-3.5 h-3.5" /> Expense
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 bg-slate-100 px-2 py-0.5 rounded">
                          <FileText className="w-3.5 h-3.5" /> Invoice
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {row.vendor}
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900">
                      {formatAmount(row.amountCents, row.currency)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={row.status} />
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      <span
                        title={
                          row.submittedBy
                            ? (memberMap.get(row.submittedBy) ??
                              row.submittedBy)
                            : undefined
                        }
                        className={
                          isSelf ? "font-medium text-slate-900" : undefined
                        }
                      >
                        {submitterDisplay(row.submittedBy, memberMap)}
                        {isSelf && (
                          <span className="ml-1 text-xs text-slate-400">
                            (you)
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {formatDate(row.submittedAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {/* Per-row approval gate: expense rows need
                          approve:expenses, invoice rows need
                          approve:invoices. Self-approval is allowed at the
                          base capability under phase-1 defaults; phase 3
                          splits on submittedBy === caller via the policy
                          engine. Employees / non-approvers never reach this
                          table because the page-level guard above redirects
                          them to /inbox. */}
                      {isPending &&
                        (row.kind === "expense"
                          ? can("approve:expenses")
                          : can("approve:invoices")) && (
                        <div className="inline-flex gap-2">
                          <button
                            type="button"
                            data-testid={`review-approve-${row.id}`}
                            disabled={busy === row.id}
                            onClick={() => {
                              const ok =
                                typeof window !== "undefined"
                                  ? window.confirm("Approve this item?")
                                  : true;
                              if (!ok) return;
                              void handleApprove(row);
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 rounded-md"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                          </button>
                          <button
                            type="button"
                            data-testid={`review-reject-${row.id}`}
                            disabled={busy === row.id}
                            onClick={() => openReject(row)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-slate-300 rounded-md"
                          >
                            <XCircle className="w-3.5 h-3.5" /> Reject
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {/* Reminder banner when browsing non-pending items while there are still pending ones */}
      {statusFilter !== "pending" && pendingCount > 0 && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          {pendingCount} item{pendingCount !== 1 ? "s" : ""} still awaiting
          review.{" "}
          <button
            type="button"
            className="underline font-medium"
            onClick={() => setStatusFilter("pending")}
          >
            Switch to Pending
          </button>
        </p>
      )}

      <Dialog
        open={rejectingRow !== null}
        onOpenChange={(next) => {
          if (!next) {
            setRejectingRow(null);
            setRejectReason("");
          }
        }}
      >
        <DialogContent
          data-testid="review-reject-modal"
          className="sm:max-w-md"
        >
          <DialogHeader>
            <DialogTitle>
              Reject{" "}
              {rejectingRow?.kind === "expense" ? "expense" : "invoice"}?
            </DialogTitle>
            <DialogDescription>
              The submitter will see this reason on their{" "}
              {rejectingRow?.kind === "expense" ? "expense" : "invoice"} and
              can resubmit after fixing.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <textarea
              data-testid="review-reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              placeholder="Explain what needs to change…"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0070F2]"
            />
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => {
                setRejectingRow(null);
                setRejectReason("");
              }}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-md"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="review-reject-submit"
              disabled={!rejectReason.trim() || busy !== null}
              onClick={() => {
                void submitReject();
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-slate-300 rounded-md"
            >
              Reject
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
